import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Edit, Trash2, Search } from 'lucide-react';
import { useCustomFields, CustomFieldsForm, CustomFieldsFilter } from '../../components/CustomFieldsRenderer';
import ExportButton from '../../components/ExportButton';
import PDFExportButton from '../../components/PDFExportButton';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [customFilterValues, setCustomFilterValues] = useState({});
  const customFields = useCustomFields('customers');
  const [customValues, setCustomValues] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    gstin: '',
    pan: '',
    billing_address: {
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India'
    },
    shipping_address: null,
    credit_limit: 0,
    payment_terms: 'Net 30'
  });
  const [sameAddress, setSameAddress] = useState(true);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/purchase/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      email: customer.email,
      phone: customer.phone,
      gstin: customer.gstin || '',
      pan: customer.pan || '',
      billing_address: customer.billing_address,
      shipping_address: customer.shipping_address,
      credit_limit: customer.credit_limit,
      payment_terms: customer.payment_terms
    });
    setSameAddress(JSON.stringify(customer.billing_address) === JSON.stringify(customer.shipping_address));
    setCustomValues(customer.custom_fields || {});
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingCustomer(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      gstin: '',
      pan: '',
      billing_address: {
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India'
      },
      shipping_address: null,
      credit_limit: 0,
      payment_terms: 'Net 30'
    });
    setSameAddress(true);
    setCustomValues({});
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        shipping_address: sameAddress ? formData.billing_address : formData.shipping_address
      };
      
      if (editingCustomer) {
        await api.put(`/purchase/customers/${editingCustomer.id}`, submitData);
        if (Object.keys(customValues).length > 0) {
          await api.put(`/config/custom-field-values/customers/${editingCustomer.id}`, customValues);
        }
      } else {
        const res = await api.post('/purchase/customers', submitData);
        if (Object.keys(customValues).length > 0 && res.data?.id) {
          await api.put(`/config/custom-field-values/customers/${res.data.id}`, customValues);
        }
      }
      
      setShowModal(false);
      fetchCustomers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save customer');
    }
  };

  const deleteCustomer = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete customer "${name}"?`)) return;
    
    try {
      await api.delete(`/purchase/customers/${id}`);
      fetchCustomers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete customer');
    }
  };

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = 
      customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.customer_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (customer.gstin && customer.gstin.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || customer.status === statusFilter;
    
    const matchesCustom = Object.entries(customFilterValues).every(([key, val]) => {
      if (!val) return true;
      const cfVal = String(customer.custom_fields?.[key] || '').toLowerCase();
      return cfVal.includes(String(val).toLowerCase());
    });
    
    return matchesSearch && matchesStatus && matchesCustom;
  });

  return (
    <div data-testid="customers-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Customer Master</h1>
        <div className="flex gap-2">
          <ExportButton endpoint="/export/customers" filename="customers_export.xlsx" />
          <PDFExportButton endpoint="/export/pdf/customers" filename="customers_export.pdf" />
          <button onClick={openCreateModal} className="btn btn-primary" data-testid="add-customer-btn">
            <Plus size={16} />
            Add Customer
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="grid grid-cols-2" style={{ gap: '16px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <div style={{ position: 'relative' }}>
              <Search
                size={16}
                style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }}
              />
              <input
                type="text"
                className="form-input"
                placeholder="Search by name, email, code, or VAT No..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ paddingLeft: '40px' }}
                data-testid="search-input"
              />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="status-filter"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <CustomFieldsFilter fields={customFields} values={customFilterValues} onChange={setCustomFilterValues} />
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div data-testid="loading-indicator">Loading customers...</div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>VAT No</th>
                  <th>City</th>
                  <th>Payment Terms</th>
                  <th className="text-right">Credit Limit</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((customer) => (
                  <tr key={customer.id} data-testid={`customer-row-${customer.customer_code}`}>
                    <td className="font-mono">{customer.customer_code}</td>
                    <td>{customer.name}</td>
                    <td className="text-sm">{customer.email}</td>
                    <td className="text-sm">{customer.phone}</td>
                    <td className="font-mono text-sm">{customer.gstin || '-'}</td>
                    <td>{customer.billing_address?.city}</td>
                    <td className="text-sm">{customer.payment_terms}</td>
                    <td className="text-right tabular-nums">₹{customer.credit_limit.toLocaleString()}</td>
                    <td>
                      <span className={`badge ${customer.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
                        {customer.status}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditModal(customer)}
                          className="btn btn-ghost btn-sm"
                          title="Edit"
                          data-testid={`edit-btn-${customer.customer_code}`}
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          onClick={() => deleteCustomer(customer.id, customer.name)}
                          className="btn btn-ghost btn-sm"
                          title="Delete"
                          data-testid={`delete-btn-${customer.customer_code}`}
                        >
                          <Trash2 size={14} color="#DC2626" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" data-testid="customer-modal">
          <div className="modal-content" style={{ maxWidth: '800px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>
                {editingCustomer ? 'Edit Customer' : 'Add Customer'}
              </h2>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      className="form-input"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3">
                  <div className="form-group">
                    <label className="form-label">Phone *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">VAT No</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.gstin}
                      onChange={(e) => setFormData({ ...formData, gstin: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">TIN</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.pan}
                      onChange={(e) => setFormData({ ...formData, pan: e.target.value })}
                    />
                  </div>
                </div>

                <h3 style={{ fontSize: '14px', fontWeight: 600, marginTop: '16px', marginBottom: '8px' }}>Billing Address</h3>
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Address Line 1 *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.billing_address.address_line1}
                      onChange={(e) => setFormData({
                        ...formData,
                        billing_address: { ...formData.billing_address, address_line1: e.target.value }
                      })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Address Line 2</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.billing_address.address_line2}
                      onChange={(e) => setFormData({
                        ...formData,
                        billing_address: { ...formData.billing_address, address_line2: e.target.value }
                      })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3">
                  <div className="form-group">
                    <label className="form-label">City *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.billing_address.city}
                      onChange={(e) => setFormData({
                        ...formData,
                        billing_address: { ...formData.billing_address, city: e.target.value }
                      })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">State *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.billing_address.state}
                      onChange={(e) => setFormData({
                        ...formData,
                        billing_address: { ...formData.billing_address, state: e.target.value }
                      })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Pincode *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.billing_address.pincode}
                      onChange={(e) => setFormData({
                        ...formData,
                        billing_address: { ...formData.billing_address, pincode: e.target.value }
                      })}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                    <input
                      type="checkbox"
                      checked={sameAddress}
                      onChange={(e) => setSameAddress(e.target.checked)}
                    />
                    Shipping address same as billing
                  </label>
                </div>

                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Credit Limit</label>
                    <input
                      type="number"
                      className="form-input"
                      value={formData.credit_limit}
                      onChange={(e) => setFormData({ ...formData, credit_limit: parseFloat(e.target.value) })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Payment Terms</label>
                    <select
                      className="form-select"
                      value={formData.payment_terms}
                      onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                    >
                      <option value="Net 15">Net 15</option>
                      <option value="Net 30">Net 30</option>
                      <option value="Net 45">Net 45</option>
                      <option value="Net 60">Net 60</option>
                    </select>
                  </div>
                </div>

                <CustomFieldsForm fields={customFields} values={customValues} onChange={setCustomValues} />
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingCustomer ? 'Update Customer' : 'Add Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;
